import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { getFocalPosition } from 'src/app/shared/utility/focal-point';

export interface FocalPointEditorData {
  imageUrl: string;
  focalX?: number;
  focalY?: number;
}

export interface FocalPointEditorResult {
  focalX: number;
  focalY: number;
}

const CENTER_PERCENT = 50;
const ARROW_STEP = 1;
const ARROW_STEP_LARGE = 10;

/**
 * Places the focal point used to crop an image's tiles.
 *
 * Deliberately not built on ResizeImageComponent: that one produces a cropped Blob that
 * gets uploaded as a new file, while this one produces two numbers and never touches the
 * image bytes.
 *
 * The preview pane reuses getFocalPosition — the same helper the gallery tile uses — so
 * what the editor sees and what the grid renders cannot drift apart.
 */
@Component({
  selector: 'app-focal-point-editor',
  templateUrl: './focal-point-editor.component.html',
  styleUrls: ['./focal-point-editor.component.scss']
})
export class FocalPointEditorComponent {
  @ViewChild('sourceSurface') sourceSurface: ElementRef<HTMLElement>;

  focalX = CENTER_PERCENT;
  focalY = CENTER_PERCENT;

  /** True while the pointer is held down, so mousemove only tracks during a drag. */
  private dragging = false;

  constructor(
    private dialogRef: MatDialogRef<FocalPointEditorComponent, FocalPointEditorResult>,
    @Inject(MAT_DIALOG_DATA) public data: FocalPointEditorData
  ) {
    this.focalX = this.clamp(data?.focalX ?? CENTER_PERCENT);
    this.focalY = this.clamp(data?.focalY ?? CENTER_PERCENT);
  }

  get imageUrl(): string {
    return this.data?.imageUrl ?? '';
  }

  /** Drives the square preview exactly as the gallery tile is driven. */
  get previewPosition(): string {
    return getFocalPosition({ focalX: this.focalX, focalY: this.focalY });
  }

  get readout(): string {
    return `${Math.round(this.focalX)}% / ${Math.round(this.focalY)}%`;
  }

  // --- pointer ---

  onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.dragging = true;
    this.applyFromClientPoint(event.clientX, event.clientY);
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.dragging) {
      return;
    }
    event.preventDefault();
    this.applyFromClientPoint(event.clientX, event.clientY);
  }

  endDrag(): void {
    this.dragging = false;
  }

  // --- touch ---

  onTouchStart(event: TouchEvent): void {
    this.applyFromTouch(event);
  }

  onTouchMove(event: TouchEvent): void {
    this.applyFromTouch(event);
  }

  // --- keyboard ---

  onKeyDown(event: KeyboardEvent): void {
    const step = event.shiftKey ? ARROW_STEP_LARGE : ARROW_STEP;

    switch (event.key) {
      case 'ArrowLeft':
        this.nudge(-step, 0);
        break;
      case 'ArrowRight':
        this.nudge(step, 0);
        break;
      case 'ArrowUp':
        this.nudge(0, -step);
        break;
      case 'ArrowDown':
        this.nudge(0, step);
        break;
      default:
        return;
    }

    // Only reached for a handled arrow key: stop the dialog from scrolling.
    event.preventDefault();
  }

  // --- actions ---

  recenter(): void {
    this.focalX = CENTER_PERCENT;
    this.focalY = CENTER_PERCENT;
  }

  save(): void {
    this.dialogRef.close({ focalX: this.focalX, focalY: this.focalY });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // --- internals ---

  private nudge(deltaX: number, deltaY: number): void {
    this.focalX = this.clamp(this.focalX + deltaX);
    this.focalY = this.clamp(this.focalY + deltaY);
  }

  private applyFromTouch(event: TouchEvent): void {
    const touch = event.touches && event.touches.length ? event.touches[0] : null;
    if (!touch) {
      return;
    }
    // Prevent the page from panning while the user drags the crosshair.
    event.preventDefault();
    this.applyFromClientPoint(touch.clientX, touch.clientY);
  }

  private applyFromClientPoint(clientX: number, clientY: number): void {
    const element = this.sourceSurface?.nativeElement;
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    this.focalX = this.clamp(((clientX - rect.left) / rect.width) * 100);
    this.focalY = this.clamp(((clientY - rect.top) / rect.height) * 100);
  }

  private clamp(value: number): number {
    if (typeof value !== 'number' || !isFinite(value)) {
      return CENTER_PERCENT;
    }
    return Math.min(100, Math.max(0, value));
  }
}
